/**
 * 「预习卡」的**纯逻辑**：把一次听写的题目装配成"每个字要写什么、读什么"。
 *
 * 这一层存在的理由与 `dictationGrading.ts` / `dictationBuild.ts` 一样：
 * 预习卡上最要紧的两件事（**逐字拼音**、**这个词是不是错词 / 有没有录音**）
 * 都是"看着对、其实差一点"的地方，而它们都能用纯函数锁住。
 *
 * ## 逐字拼音：唯一的口径是**按字对齐整词拼音**
 *
 * 后端给的一道题只有两个字段可用（见 `api/dictationSessions.ts` 的 `DictationQuestionView`）：
 *
 *   · `wordText` —— 词面（快照，词被改/删之后历史会话仍然可读）；
 *   · `pinyin`   —— **出题那一刻的整词拼音快照**（音节用空格分隔）。
 *
 * 它已经是"人工覆盖优先"之后的值：字表里没人工改过就是内置表逐字拼出来的
 * （`joinWordPinyin()`），改过就是家长写的那一串（**按词定音** —— 多音字唯一可靠的
 * 读法来源，例如「长大 / cháng dà」而不是默认的 zhǎng dà）。所以：
 *
 *   **第 N 个音节就是第 N 个字的读音**（`position` 与 `word_hanzi.position` 同口径，
 *   都从 1 开始）。多音字于是天然正确：整词覆盖是按词给的，逐字分配只是把它切开。
 *
 * ### 音节数对不上时**不猜**
 *
 * `joinWordPinyin()` 给自动值时永远是 N 个槽位（某个字没收录就是空槽，用连续两个
 * 空格占位），所以"对不上"只可能来自**人工覆盖写错了**（少写/多写一个音节，或者
 * 干脆没打空格 —— 例如把「蝴蝶」写成 `húdié`）。
 *
 * 这种情况下**刻意不用内置表补一个读音**：
 *
 *   · 用内置表补，等于用"最常用读音"顶替家长写的那一串 —— 而多音字的默认值
 *     恰恰经常是错的（`bank` 式的坑：「银行」默认 yín xíng）。预习卡的用途是
 *     **看着字学怎么写、怎么读**，显示一个与家长本意不同的音就是**教错**；
 *   · 所以逐字读音一律给 `null`（界面上是「—」），并由页面明说"整词拼音的音节数
 *     和字数对不上"，让家长回字表改。**宁可显式缺一个音，也不要编一个音。**
 *
 * ## 三个状态必须分得开（本项目反复踩过的坑）
 *
 *   · `mistake === null`   —— 确实不在错词本里（第一次听写这个词 / 已经掌握）；
 *   · `mistake === undefined` —— **字段没拿到**（后端没给 / 响应形状变了）；
 *   · `hasAudio === null`  —— **"有没有录音"这个字段没拿到**（与 `false` = 确实没录分开）。
 *
 * 界面上这三态各自有话说（见 `ParentDictationPreviewPage`），绝不合并成一句
 * "没有录音 / 没错过"。
 */
import type {
  DictationQuestionView,
  QuestionMistakeView,
} from './api/dictationSessions';

/** 一个字在预习卡上的全部信息。 */
export interface PreviewChar {
  /** 字本身（单字符；词里混进的字母/数字也照原样算一个字）。 */
  char: string;
  /** 在词里的位置，**从 1 开始**（与 `word_hanzi.position` / `dictation_question.position` 同口径）。 */
  position: number;
  /**
   * 这个字要读什么（含声调）。
   * `null` = **拿不到**（整词拼音为空，或音节数与字数对不上）—— 界面上显示「—」。
   */
  pinyin: string | null;
}

/** 一道题在预习卡上的样子。 */
export interface PreviewWord {
  questionId: string;
  /** 题号，从 1 开始（与播放器上的"第 N 题"一致）。 */
  position: number;
  wordText: string;
  /** 后端给的整词拼音**原文**（空串 = 这个词没有读音）。 */
  wordPinyin: string;
  /** 逐字读音是不是真的从整词拼音切出来的（`false` 见文件头"音节数对不上"）。 */
  pinyinAligned: boolean;
  chars: PreviewChar[];
  /** 指向字表里的词；词被删过则是 `null`（此时取不了录音）。 */
  wordId: string | null;
  /** `null` = 确实不在错词本里；`undefined` = 字段没拿到。 */
  mistake: QuestionMistakeView | null | undefined;
  /** `true`/`false` = 有/没有家长录音；`null` = 字段没拿到。 */
  hasAudio: boolean | null;
}

/**
 * 整词拼音 → 逐字槽位（**保留空槽**）。
 *
 * 分隔符认三种：半角空格、制表符/全角空格（手工粘贴来的值常见）、连字符
 * （`zhǎng-dà`，与 `@fht/shared` 的 `splitSyllables()` 同一口径）。
 *
 * ⚠️ **不能把连续空格折叠掉**：自动值里"某个字没收录"就是用连续两个空格占位的，
 *   折叠之后位置就整体左移，第 3 个字的读音会跑到第 2 个字头上 —— 那是"看着像
 *   对了"的错误，比缺一个音严重得多。
 *
 * 例：
 *   splitWordPinyinSlots('xué xí')      → ['xué', 'xí']
 *   splitWordPinyinSlots('xué  xí')     → ['xué', '', 'xí']   // 中间那个字没读音
 *   splitWordPinyinSlots('zhǎng-dà')    → ['zhǎng', 'dà']
 */
export function splitWordPinyinSlots(pinyin: string): string[] {
  return pinyin.replace(/[-\t\u3000]/g, ' ').split(' ');
}

/**
 * 逐字读音分配。返回的 `aligned` 说明"这次切开了没有"。
 *
 * 规则（就这一条）：
 *   · 整词拼音为空 → 每个字 `null`，`aligned: false`；
 *   · 槽位数 **等于** 字数 → 第 N 个槽位给第 N 个字（空槽 = 那个字没读音 → `null`），
 *     非空的槽位 `aligned: true`；
 *   · 槽位数 **不等于** 字数 → **一个都不猜**：全部 `null`，`aligned: false`。
 */
export function assignCharPinyin(
  wordText: string,
  wordPinyin: string,
): { chars: PreviewChar[]; aligned: boolean } {
  const text = [...wordText];
  const raw = wordPinyin;

  if (raw === '' || text.length === 0) {
    return {
      chars: text.map((char, index) => ({ char, position: index + 1, pinyin: null })),
      aligned: false,
    };
  }

  const slots = splitWordPinyinSlots(raw);
  /**
   * 槽位数对不上、或者**一个音节都没有**（例如整串只有一个空格）：都算"没对上"。
   * 后者若算成 `aligned: true`，页面上就不会提示"音节数和字数对不上"，
   * 而每个字头上都是「—」—— 家长看不到任何可以动手修的地方。
   */
  if (slots.length !== text.length || !slots.some((slot) => slot.trim() !== '')) {
    return {
      chars: text.map((char, index) => ({ char, position: index + 1, pinyin: null })),
      aligned: false,
    };
  }

  return {
    chars: text.map((char, index) => {
      const syllable = slots[index]?.trim() ?? '';
      return { char, position: index + 1, pinyin: syllable === '' ? null : syllable };
    }),
    aligned: true,
  };
}

/**
 * 题列表 → 预习卡列表。
 *
 * ⚠️ **原样保留每一道题**（不排序、不去重、不跳过"没有读音"的）：
 *   预习卡是"这一轮要听写的词都在这里，照着看一遍"，少一个词就是漏一道题。
 *   顺序也照后端给的（就是听写时的题号顺序），排序交给调用方反而会漂。
 */
export function buildPreviewWords(
  questions: readonly DictationQuestionView[],
): PreviewWord[] {
  return questions.map((question) => {
    const { chars, aligned } = assignCharPinyin(question.wordText, question.pinyin);
    return {
      questionId: question.id,
      position: question.position,
      wordText: question.wordText,
      wordPinyin: question.pinyin,
      pinyinAligned: aligned,
      chars,
      wordId: question.wordId,
      mistake: question.mistake,
      hasAudio: question.hasAudio,
    };
  });
}

/** 预习卡的汇总数字（页面顶部那行徽标用）。 */
export interface PreviewSummary {
  /** 词数（= 题数）。 */
  wordCount: number;
  /** 字数（所有词的字数之和）。 */
  charCount: number;
  /** **没拿到读音**的字数（界面上显示成「—」的那些）。 */
  charsWithoutPinyin: number;
  /** 当前还在错词本里的**词**数。 */
  mistakeWordCount: number;
  /** **错词记录没拿到**的词数（`mistake === undefined`，与"没错过"是两件事）。 */
  unknownMistakeWordCount: number;
  /** 有家长录音的词数。 */
  audioWordCount: number;
  /** **录音情况没拿到**的词数（`hasAudio === null`）。 */
  unknownAudioWordCount: number;
}

export function summarizePreview(words: readonly PreviewWord[]): PreviewSummary {
  const summary: PreviewSummary = {
    wordCount: words.length,
    charCount: 0,
    charsWithoutPinyin: 0,
    mistakeWordCount: 0,
    unknownMistakeWordCount: 0,
    audioWordCount: 0,
    unknownAudioWordCount: 0,
  };

  for (const word of words) {
    summary.charCount += word.chars.length;
    for (const char of word.chars) {
      if (char.pinyin === null) summary.charsWithoutPinyin += 1;
    }
    if (word.mistake === undefined) summary.unknownMistakeWordCount += 1;
    else if (word.mistake !== null) summary.mistakeWordCount += 1;
    if (word.hasAudio === null) summary.unknownAudioWordCount += 1;
    else if (word.hasAudio) summary.audioWordCount += 1;
  }

  return summary;
}

/**
 * 一道题的"错词情况" → 人话（预习卡上的徽标）。
 *
 * 与批改页的 `describeQuestionHistory()` 刻意分开：那边是给家长**批改时**看的
 * （"以前写错 3 次"），这边是给孩子**预习时**看的 —— 同样的事实，但预习卡上
 * 不该出现"没写出来几次"这种批改口径的措辞。
 */
export function describePreviewMistake(word: PreviewWord): string {
  if (word.mistake === undefined) return '错词情况未获取';
  if (word.mistake === null) return '';
  const times = word.mistake.wrongCount + word.mistake.missingCount;
  if (times <= 0) return '在错词本里';
  return `错词（错过 ${times} 次）`;
}

/** 一个字的读音在界面上怎么显示。`null` → 「—」（**不是空串**，空串看起来像排版坏了）。 */
export function pinyinLabel(pinyin: string | null): string {
  return pinyin === null ? '—' : pinyin;
}
